const CAMERA_READY_TIMEOUT_MS = 5_000;
export const CAMERA_STABILIZATION_MS = 1_200;
const CAMERA_IDEAL_WIDTH = 3_840;
const CAMERA_IDEAL_HEIGHT = 2_160;
const MAX_OUTPUT_DIMENSION = 2_560;
const MAX_OUTPUT_BYTES = 2 * 1024 * 1024;
const MIN_OUTPUT_DIMENSION = 800;
const JPEG_QUALITIES = [0.92, 0.85, 0.78, 0.7] as const;

type FocusCapableMediaTrackCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
};

type FocusCapableMediaTrackConstraintSet = MediaTrackConstraintSet & {
  focusMode?: string;
};

export interface CameraVisibleCrop {
  readonly viewportAspectRatio: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export async function requestCameraStream(
  facingMode: 'environment' | 'user',
): Promise<MediaStream> {
  const video: MediaTrackConstraints = {
    facingMode: { ideal: facingMode },
    width: { ideal: CAMERA_IDEAL_WIDTH },
    height: { ideal: CAMERA_IDEAL_HEIGHT },
  };

  const stream = await navigator.mediaDevices.getUserMedia({
    video,
    audio: false,
  });

  await enableContinuousFocus(stream.getVideoTracks()[0]);
  return stream;
}

export async function captureHighQualityPhoto(
  video: HTMLVideoElement,
  visibleCrop?: CameraVisibleCrop,
): Promise<Blob> {
  const frozenFrame = document.createElement('canvas');
  frozenFrame.width = video.videoWidth;
  frozenFrame.height = video.videoHeight;
  const frozenContext = frozenFrame.getContext('2d');
  if (!frozenContext) throw new Error('No se pudo congelar la captura.');
  frozenContext.drawImage(video, 0, 0, frozenFrame.width, frozenFrame.height);

  const crop = resolveVisibleCrop(frozenFrame.width, frozenFrame.height, visibleCrop);
  const outputScale = Math.min(
    1,
    MAX_OUTPUT_DIMENSION / Math.max(crop.width, crop.height),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(crop.width * outputScale));
  canvas.height = Math.max(1, Math.round(crop.height * outputScale));
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('No se pudo preparar la captura.');
  }

  context.drawImage(
    frozenFrame,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  frozenFrame.width = 0;
  frozenFrame.height = 0;

  return compressCanvasAdaptively(canvas);
}

export async function prepareCameraVideo(
  video: HTMLVideoElement,
  stream: MediaStream,
): Promise<void> {
  video.srcObject = stream;
  await video.play();

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth === 0) {
    await waitForVideoData(video);
  }

  await delay(CAMERA_STABILIZATION_MS);
}

export function stopMediaStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => track.stop());
}

function resolveVisibleCrop(
  sourceWidth: number,
  sourceHeight: number,
  visibleCrop?: CameraVisibleCrop,
): { x: number; y: number; width: number; height: number } {
  if (!visibleCrop || visibleCrop.viewportAspectRatio <= 0) {
    return { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
  }

  const viewportWidth = 1;
  const viewportHeight = 1 / visibleCrop.viewportAspectRatio;
  const coverScale = Math.max(viewportWidth / sourceWidth, viewportHeight / sourceHeight);
  const renderedWidth = sourceWidth * coverScale;
  const renderedHeight = sourceHeight * coverScale;
  const hiddenX = (renderedWidth - viewportWidth) / 2;
  const hiddenY = (renderedHeight - viewportHeight) / 2;

  const x = Math.round((visibleCrop.x * viewportWidth + hiddenX) / coverScale);
  const y = Math.round((visibleCrop.y * viewportHeight + hiddenY) / coverScale);
  const width = Math.round((visibleCrop.width * viewportWidth) / coverScale);
  const height = Math.round((visibleCrop.height * viewportHeight) / coverScale);
  const clampedX = clamp(x, 0, sourceWidth - 1);
  const clampedY = clamp(y, 0, sourceHeight - 1);

  return {
    x: clampedX,
    y: clampedY,
    width: clamp(width, 1, sourceWidth - clampedX),
    height: clamp(height, 1, sourceHeight - clampedY),
  };
}

async function compressCanvasAdaptively(sourceCanvas: HTMLCanvasElement): Promise<Blob> {
  let canvas = sourceCanvas;
  let lastBlob: Blob | null = null;

  while (true) {
    for (const quality of JPEG_QUALITIES) {
      lastBlob = await canvasToBlob(canvas, quality);
      if (lastBlob.size <= MAX_OUTPUT_BYTES) return lastBlob;
    }

    if (Math.min(canvas.width, canvas.height) <= MIN_OUTPUT_DIMENSION) {
      if (!lastBlob) throw new Error('No se pudo comprimir la captura.');
      return lastBlob;
    }

    if (!lastBlob) throw new Error('No se pudo comprimir la captura.');
    const minimumScale = MIN_OUTPUT_DIMENSION / Math.min(canvas.width, canvas.height);
    const sizeScale = Math.max(
      minimumScale,
      Math.min(0.85, Math.sqrt(MAX_OUTPUT_BYTES / lastBlob.size) * 0.95),
    );
    const nextCanvas = document.createElement('canvas');
    nextCanvas.width = Math.round(canvas.width * sizeScale);
    nextCanvas.height = Math.round(canvas.height * sizeScale);
    const context = nextCanvas.getContext('2d');
    if (!context) return lastBlob;
    context.drawImage(canvas, 0, 0, nextCanvas.width, nextCanvas.height);
    canvas.width = 0;
    canvas.height = 0;
    canvas = nextCanvas;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('No se pudo generar la captura.'));
      },
      'image/jpeg',
      quality,
    );
  });
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

async function enableContinuousFocus(track: MediaStreamTrack | undefined): Promise<void> {
  if (!track?.getCapabilities) return;

  const capabilities = track.getCapabilities() as FocusCapableMediaTrackCapabilities;
  if (!capabilities.focusMode?.includes('continuous')) return;

  const currentConstraints = track.getConstraints();
  const continuousFocus: FocusCapableMediaTrackConstraintSet = {
    focusMode: 'continuous',
  };

  try {
    await track.applyConstraints({
      ...currentConstraints,
      advanced: [...(currentConstraints.advanced ?? []), continuousFocus],
    });
  } catch {
    // El soporte depende del navegador y del sensor. Conservamos el stream
    // negociado si el dispositivo rechaza el modo de enfoque solicitado.
  }
}

function waitForVideoData(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('La camara no entrego video a tiempo.'));
    }, CAMERA_READY_TIMEOUT_MS);

    const onLoadedData = (): void => {
      cleanup();
      resolve();
    };

    const onError = (): void => {
      cleanup();
      reject(new Error('No se pudo reproducir el video de la camara.'));
    };

    const cleanup = (): void => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('error', onError);
    };

    video.addEventListener('loadeddata', onLoadedData, { once: true });
    video.addEventListener('error', onError, { once: true });
  });
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
